import { useMemo } from 'react'
import { useAppSelector } from '../../state/hooks'
import {
  selectSaleId,
  selectSaleStatus,
} from '../../state/slices/currentSale/currentSaleSlice'
import { FEATURE_ALLOW_EDIT_UNFULFILLED_SALES } from '../../constants'
import { isFulfilledLineItem, isParked } from '../../functions/sale'
import { FulfilmentType } from '../../models/fulfilment'
import { LineItem, UnknownLineItem } from '../../models/line-item'
import { getFeatureFromCache } from '../../util/feature-cache'

/**
 * Type guard that distinguishes a known `LineItem` from an `UnknownLineItem`
 * by checking for the presence of a `fulfilmentType` property.
 */
const isLineItem = (
  lineItem: LineItem | UnknownLineItem
): lineItem is LineItem => 'fulfilmentType' in lineItem

/**
 * Result of grouping basket items using the "legacy" strategy, where items
 * are bucketed purely by fulfilment type without regard to whether they've
 * already been fulfilled.
 */
interface LegacyBasketGroups {
  /** Items with no fulfilment type (or unknown line items). */
  nonFulfillmentLineItems: Array<LineItem | UnknownLineItem>
  /** Items to be picked up (includes delivery items for service sales). */
  pickUpLineItems: LineItem[]
  /** Items to be delivered. */
  deliveryLineItems: LineItem[]
}

/**
 * Result of grouping basket items using the "fulfilled" strategy, where
 * items that have already been fulfilled (or are unknown) are separated
 * out from the remaining pick-up/delivery items.
 */
interface FulfilledBasketGroups {
  /** Items that have already been fulfilled, plus any unknown line items. */
  fulfilledLineItems: Array<LineItem | UnknownLineItem>
  /** Unfulfilled items to be picked up (includes delivery items for service sales). */
  pickUpLineItems: LineItem[]
  /** Unfulfilled items to be delivered. */
  deliveryLineItems: LineItem[]
}

/**
 * Merges known and unknown line items into a single array, sorted in
 * ascending order by the date each item was added to the basket.
 */
const sortByDateAdded = (
  lineItems: LineItem[],
  unknownLineItems: UnknownLineItem[]
) =>
  [...lineItems, ...unknownLineItems].sort((a, b) => a.dateAdded - b.dateAdded)

/**
 * Groups sorted basket items using the "legacy" grouping strategy, where
 * items are bucketed purely by their fulfilment type (pick-up vs delivery),
 * regardless of whether they have already been fulfilled. For service sales,
 * delivery items are treated as pick-up items.
 */
const groupLegacyBasketItems = (
  sortedItems: Array<LineItem | UnknownLineItem>,
  isServiceSale: boolean
): LegacyBasketGroups => {
  return sortedItems.reduce<LegacyBasketGroups>(
    (acc, item) => {
      if (!isLineItem(item)) {
        acc.nonFulfillmentLineItems.push(item)
      } else if (item.fulfilmentType === FulfilmentType.PICKUP) {
        acc.pickUpLineItems.push(item)
      } else if (item.fulfilmentType === FulfilmentType.DELIVERY) {
        if (isServiceSale) {
          acc.pickUpLineItems.push(item)
        } else {
          acc.deliveryLineItems.push(item)
        }
      } else {
        acc.nonFulfillmentLineItems.push(item)
      }
      return acc
    },
    {
      nonFulfillmentLineItems: [],
      pickUpLineItems: [],
      deliveryLineItems: [],
    }
  )
}

/**
 * Groups sorted basket items using the "fulfilled" grouping strategy, where
 * items that have already been fulfilled are separated out from the
 * remaining pick-up/delivery items. Unknown line items are always treated
 * as fulfilled. For service sales, delivery items are treated as pick-up
 * items.
 */
const groupFulfilledBasketItems = (
  sortedItems: Array<LineItem | UnknownLineItem>,
  isServiceSale: boolean
): FulfilledBasketGroups => {
  return sortedItems.reduce<FulfilledBasketGroups>(
    (acc, item) => {
      if (!isLineItem(item)) {
        acc.fulfilledLineItems.push(item)
      } else if (isFulfilledLineItem(item)) {
        acc.fulfilledLineItems.push(item)
      } else if (item.fulfilmentType === FulfilmentType.PICKUP) {
        acc.pickUpLineItems.push(item)
      } else if (item.fulfilmentType === FulfilmentType.DELIVERY) {
        if (isServiceSale) {
          acc.pickUpLineItems.push(item)
        } else {
          acc.deliveryLineItems.push(item)
        }
      }
      return acc
    },
    {
      fulfilledLineItems: [],
      pickUpLineItems: [],
      deliveryLineItems: [],
    }
  )
}

/**
 * The shape of the value returned by {@link useBasketFulfillmentGroups},
 * containing the grouped basket line items alongside grouping metadata.
 */
export interface SaleBaskets {
  /** Unfulfilled items to be delivered (legacy grouping). */
  deliveryLineItems: LineItem[]
  /** Unfulfilled items to be picked up (legacy grouping). */
  pickUpLineItems: LineItem[]
  /** Items with no fulfilment type (or unknown line items) (legacy grouping). */
  nonFulfillmentLineItems: Array<LineItem | UnknownLineItem>
  /** Items that have already been fulfilled, plus any unknown line items. */
  fulfilledLineItems: Array<LineItem | UnknownLineItem>
  /** Whether the "fulfilled" grouping should be used instead of the legacy grouping. */
  shouldUseFulfilledGrouping: boolean
}

/**
 * Hook that groups a sale's basket line items by fulfilment type (pick-up
 * or delivery) for rendering in the sale summary/basket UI.
 *
 * Computes both a "legacy" grouping (items bucketed purely by fulfilment
 * type) and a "fulfilled" grouping (items separated based on whether they've
 * already been fulfilled), gated behind the `FEATURE_ALLOW_EDIT_UNFULFILLED_SALES`
 * feature flag and whether the sale is a continued (non-parked, existing)
 * sale that isn't a continued service sale.
 *
 * @param lineItems - Known line items in the basket.
 * @param unknownLineItems - Line items whose details could not be resolved.
 * @param isServiceSale - Whether the sale is a service sale (delivery items
 * are treated as pick-up items for service sales).
 * @param isContinuedServiceSale - Whether the sale is a continued service
 * sale, which disables the fulfilled grouping.
 */
export const useBasketFulfillmentGroups = (
  lineItems: LineItem[],
  unknownLineItems: UnknownLineItem[],
  isServiceSale: boolean,
  isContinuedServiceSale: boolean
): SaleBaskets => {
  const saleStatus = useAppSelector(selectSaleStatus)
  const saleId = useAppSelector(selectSaleId)

  const isAllowEditUnfulfilledSalesEnabled = getFeatureFromCache(
    FEATURE_ALLOW_EDIT_UNFULFILLED_SALES
  )

  const isContinuedSale = Boolean(saleId) && !isParked(saleStatus)

  const shouldUseFulfilledGrouping =
    isAllowEditUnfulfilledSalesEnabled &&
    isContinuedSale &&
    !isContinuedServiceSale

  const sortedItems = useMemo(
    () => sortByDateAdded(lineItems, unknownLineItems),
    [lineItems, unknownLineItems]
  )

  const legacyGroups = useMemo(() => {
    if (shouldUseFulfilledGrouping) {
      return null
    }

    return groupLegacyBasketItems(sortedItems, isServiceSale)
  }, [isServiceSale, sortedItems, shouldUseFulfilledGrouping])

  const fulfilledGroups = useMemo(() => {
    if (!shouldUseFulfilledGrouping) {
      return null
    }

    return groupFulfilledBasketItems(sortedItems, isServiceSale)
  }, [isServiceSale, shouldUseFulfilledGrouping, sortedItems])

  // When fulfilled grouping is active, the main basket fields expose only the
  // unfulfilled pick-up/delivery items and already-fulfilled items are surfaced
  // separately via `fulfilledLineItems`. Otherwise fall back to legacy grouping.
  if (shouldUseFulfilledGrouping && fulfilledGroups) {
    return {
      deliveryLineItems: fulfilledGroups.deliveryLineItems,
      pickUpLineItems: fulfilledGroups.pickUpLineItems,
      nonFulfillmentLineItems: [],
      fulfilledLineItems: fulfilledGroups.fulfilledLineItems,
      shouldUseFulfilledGrouping,
    }
  }

  return {
    deliveryLineItems: legacyGroups?.deliveryLineItems ?? [],
    pickUpLineItems: legacyGroups?.pickUpLineItems ?? [],
    nonFulfillmentLineItems: legacyGroups?.nonFulfillmentLineItems ?? [],
    fulfilledLineItems: [],
    shouldUseFulfilledGrouping,
  }
}
